'use client';
import * as React from 'react';
import { styled } from '@linaria/react';

import Paragraph from '@/components/Paragraph';
import InlineCode from '@/components/InlineCode';
import Em from '@/components/Spicy';
import CodeSnippetProvider from '@/components/CodeSnippet/CodeSnippetProvider';
import LazyVideoGif from '@/components/VideoGif/LazyVideoGif';

import Arrow from './Arrow';
import { constructSnippet } from './ReassignableVariable.helpers';

export interface Props {
  label: string;
  choices: Array<string>;
  initialChoiceIndex?: number;
  codeOutput: React.ReactNode;
}

const MOBILE_CUTOFF = 550;

// HACK: Too lazy to come up with a smart solution
const ENDPOINT_OFFSETS = {
  1: [0],
  2: [0, 0],
  3: [-4, 0, -4],
  4: [-8, -4, -3, -7],
  5: [-8, -4, 1, -4, -8],
};

function ReassignableVariable({
  label,
  choices,
  initialChoiceIndex = 0,
  codeOutput,
}: Props) {
  const id = React.useId();

  const [choiceIndex, setChoiceIndex] = React.useState(
    initialChoiceIndex
  );
  const [hoveredChoiceIndex, setHoveredChoiceIndex] = React.useState<
    number | null
  >(null);

  const choiceRefs = React.useRef<{
    [index: number]: HTMLDivElement;
  }>({});

  // TS complains because there's no guarantee that `choices.length` will be a key in `ENDPOINT_OFFSETS`. I'm too lazy to come up with a proper solution, but it's fine.
  // @ts-ignore
  const endpointOffsets = ENDPOINT_OFFSETS[choices.length];

  const codeSnippet = constructSnippet({
    choiceIndex,
    selectedChoice: choices[choiceIndex],
  });

  return (
    <>
      <Wrapper>
        <Label>
          {' '}
          <Prefix>let</Prefix>
          {label}
        </Label>
        <ArrowWrapper>
          <Arrow
            choiceRefs={choiceRefs}
            choiceIndex={choiceIndex}
            offsetEndpointBy={endpointOffsets[choiceIndex]}
          />
        </ArrowWrapper>
        <ChoicesWrapper>
          {choices.map((choice, index) => {
            const isSelected = index === choiceIndex;
            const isHovered = index === hoveredChoiceIndex;
            return (
              <Choice
                ref={(elem) => {
                  // @ts-ignore
                  choiceRefs.current[index] = elem;
                }}
                htmlFor={`choice-${id}-${index}`}
                key={index}
              >
                <Shadow
                  data-is-highlighted={isSelected || isHovered}
                />
                <ActiveBackground
                  style={{
                    opacity: isSelected ? 1 : 0,
                  }}
                />
                <ChoiceLabel>{choice}</ChoiceLabel>
                <Radio
                  type="radio"
                  name={`reassignable-variable-${id}`}
                  id={`choice-${id}-${index}`}
                  checked={isSelected}
                  onChange={() => {
                    setChoiceIndex(index);
                  }}
                  onMouseEnter={() => {
                    setHoveredChoiceIndex(index);
                  }}
                  onMouseLeave={() => {
                    setHoveredChoiceIndex(null);
                  }}
                />
              </Choice>
            );
          })}
        </ChoicesWrapper>
      </Wrapper>
      <MobileAlternative>
        <LazyVideoGif
          includeBorder={false}
          alt="Diagram showing that the “fruits” variable is pointing to an array of fruits."
          width={960 / 2}
          height={586 / 2}
          src="/images/the-const-deception/reassignable-variable.mp4"
          thumbnailSrc="/images/the-const-deception/reassignable-thumbnail.png"
        />
      </MobileAlternative>
      <SubsequentCopy>
        <Paragraph>
          This is known as <Em>re-assignment.</Em> We're saying that
          actually, the <InlineCode>fruits</InlineCode> label should
          refer to an <i>entirely different value:</i>
        </Paragraph>
        <CodeSnippetProvider lang="js" code={codeSnippet}>
          {codeOutput}
        </CodeSnippetProvider>
      </SubsequentCopy>
    </>
  );
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 48px;

  @media (max-width: ${MOBILE_CUTOFF}px) {
    display: none;
  }
`;

const Label = styled.div`
  position: relative;
  font-size: 1.25rem;
  font-family: var(--font-family-mono);
`;

const Prefix = styled.span`
  position: absolute;
  top: 5px;
  left: 0;
  transform: translateY(-100%);
  font-size: 0.75rem;
  color: var(--color-gray-700);
`;

const ArrowWrapper = styled.div`
  flex: 1;
  align-self: stretch;
`;

const ChoicesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Choice = styled.label`
  position: relative;
  color: black;
  border-radius: 4px;
  padding: 14px 18px;
  font-family: var(--font-family-mono);
  white-space: pre-wrap;
`;

const Shadow = styled.div`
  position: absolute;
  inset: 0;
  background: white;
  border-radius: inherit;
  box-shadow:
    0px 1px 2px hsl(0deg 0% 0% / 0.1),
    0px 2px 4px hsl(0deg 0% 0% / 0.1),
    0px 4px 8px hsl(0deg 0% 0% / 0.1),
    0px 8px 16px hsl(0deg 0% 0% / 0.1);
  transition: opacity 250ms;
  will-change: opacity;
  opacity: 0.25;

  &[data-is-highlighted='true'] {
    opacity: 1;
  }

  html[data-color-mode='dark'] & {
    opacity: 0.825;

    &[data-is-highlighted='true'] {
      opacity: 1;
    }
  }
`;

const ActiveBackground = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: hsl(50deg 100% 88%);
  transition: opacity 250ms;
  will-change: opacity;
`;

const ChoiceLabel = styled.div`
  position: relative;
`;

const Radio = styled.input`
  position: absolute;
  inset: 0;
  appearance: none;
  cursor: pointer;
  border-radius: inherit;
`;

const MobileAlternative = styled.div`
  display: none;

  @media (max-width: ${MOBILE_CUTOFF}px) {
    display: block;
  }
`;

const SubsequentCopy = styled.div`
  margin-top: 64px;
`;

export default ReassignableVariable;
